import { Fragment, UIEvent, useCallback, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';

import {
  Box,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
} from '@material-ui/core';

import {
  FilterEditorComponentProps,
  SearchUtils,
  SimpleAutocomplete,
} from '@spotify/confidence-core-react';

import { useSurfaces } from '../../hooks';

const Container = styled('div')({
  maxHeight: 300,
  maxWidth: 300,
  overflowY: 'auto',
});

export const SurfaceFilterEditor = ({
  value,
  onValueChange,
  onClose,
}: FilterEditorComponentProps<string[]>) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  useDebounce(
    () => {
      setDebouncedInputValue(inputValue);
    },
    300,
    [inputValue],
  );

  const filter = SearchUtils.buildSearchFilter(
    debouncedInputValue,
    'displayName',
  );

  const { surfaces, isLoading, hasNextPage, nextPage, isLoadingNextPage } =
    useSurfaces({
      pageSize: 25,
      filter,
      orderBy: 'displayName:asc',
    });

  const handleScroll = useCallback(
    (event: UIEvent<HTMLUListElement>) => {
      const listbox = event.currentTarget;
      const isNearBottom =
        listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50;
      if (isNearBottom && hasNextPage && !isLoading && !isLoadingNextPage) {
        nextPage();
      }
    },
    [hasNextPage, isLoading, isLoadingNextPage, nextPage],
  );

  return (
    <Container>
      <SimpleAutocomplete.Root
        multiple
        disableFilter
        items={
          surfaces?.map(surface => ({
            value: surface.name,
            label: surface.displayName,
            surface,
          })) ?? []
        }
        value={value ?? []}
        onValueChange={onValueChange}
        onInputChange={setInputValue}
        // when someone selects from the list, close the editor
        onSelect={() => {
          onClose();
          return false;
        }}
      >
        {({ items, getInputProps, getListboxProps, getOptionProps }) => (
          <Fragment>
            <SimpleAutocomplete.Input
              data-track-source-anchor="f589d9787a" {...getInputProps()}
              placeholder="Search surfaces..."
              fullWidth
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              margin="dense"
            />
            <SimpleAutocomplete.Empty />

            <List
              {...getListboxProps()}
              style={{ maxHeight: 250, overflowY: 'auto' }}
              onScroll={handleScroll}
            >
              {items.map((item, index) => (
                <ListItem
                  key={item.value}
                  button
                  disableGutters
                  {...getOptionProps(index)}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={value?.includes(item.value) ?? false}
                      onClick={e => e.stopPropagation()}
                      onChange={e => {
                        if (e.target.checked) {
                          onValueChange([...(value ?? []), item.value]);
                        } else {
                          onValueChange(
                            value?.filter(v => v !== item.value) ?? [],
                          );
                        }
                      }}
                      tabIndex={-1}
                      disableRipple
                     data-track-source-anchor="2cf1aff462;surface-filter-editor-checkbox;low"/>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.surface.displayName}
                    primaryTypographyProps={{
                      noWrap: true,
                    }}
                    secondary={item.surface.description ?? '(No description)'}
                    secondaryTypographyProps={{
                      noWrap: true,
                    }}
                  />
                </ListItem>
              ))}
              {hasNextPage && (isLoading || isLoadingNextPage) && (
                <Box display="flex" justifyContent="center" py={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </List>
          </Fragment>
        )}
      </SimpleAutocomplete.Root>
    </Container>
  );
};
